import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import AppReceipt from "../../components/app-receipts/PrintReceipt";
import CustomerInfoChangeDialog from "../../components/customer-info-change-dialog/CustomerInfoChangeDialog";
import DiscountDialog from "../../components/discount-dialog/DiscountDialog";
import Iconify from "../../components/iconify/Iconify";
import InstructionsDialog from "../../components/instructions-dialog/InstructionsDialog";
import OrderBillDetails from "../../components/order-bill-details/OrderBillDetails";
import OrderCancelConfirmDialog from "../../components/order-cancel-confirm-dialog/OrderCancelConfirmDialog";
import OrderItemDeleteConfirmDialog from "../../components/order-item-delete-confirmation-dialog/OrderItemDeleteConfirmDialog";
import OrderKOTViewDrawer from "../../components/order-kot-view-drawer/OrderKOTViewDrawer";
import OrderMenuItem from "../../components/order-menu-item/OrderMenuItem";
import OrderTypeSelector from "../../components/order-type-selector/OrderTypeSelector";
import Scrollbar from "../../components/scrollbar/Scrollbar";
import TableChangeDialog from "../../components/table-change-dialog/TableChangeDialog";
import { SITE_TITLE } from "../../constants/Site";
import {
  selectBillTotals,
  selectFinalTotals,
  selectMergedItems,
} from "../../reduxStore/selectors/cartSelectors";
import {
  addItem,
  cancelOrder,
  clearDiscount,
  clearPendingItems,
  clearSelectedTable,
  deleteOrderItem,
  getTablesWithStatus,
  removeItem,
  removeNote,
  removeOrderCustomer,
  resetCartKeepOrderType,
  setLastAddedItemId,
  setOrderType,
  updateItem,
} from "../../reduxStore/slices/cartSlice";

import { useNavigate } from "react-router-dom";
import {
  ORDER_STATUS,
  ORDER_TYPES_VALUES,
  ORDER_TYPE_CODES,
} from "../../constants/enums";
import { formatTime } from "../../utils/formatTime";
import { useNotify } from "../../utils/snackbarUtils";
import CategoryList from "./CategoryList";
import EmptyCartState from "./EmptyCartState";
import EmptyCategoryItems from "./EmptyCategoryItems";
import FoodTypeLegend from "./FoodTypeLegend";
import ItemCard from "./ItemCard";
import ItemVariantSelectionDialog from "./ItemVariantSelectionDialog";
import "./Menu.css";
import OrderActions from "./OrderActions";
import OrderInfoBar from "./OrderInfoBar";
import OrderTypeChangeConfirmationDialog from "./OrderTypeChangeConfirmation";

function Menu(props) {
  const { showSuccess, handleApiError, showError } = useNotify();
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    activeOrderId,
    orderId,
    customerDetails,
    orderType,
    pendingItems,
    existingKots,
    selectedTable,
    lastAddedItemId,
    note: itemsNote,
    discount: orderDiscount,
  } = useSelector((state) => state.cart);

  const { loading: isLoading } = useSelector((state) => state.menu);

  const [isInstructionsDialogOpen, setIsInstructionsDialogOpen] =
    useState(false);
  const [isDiscountDialogOpen, setIsDiscountDialogOpen] = useState(false);
  const [isTableChangeDialogOpen, setIsTableChangeDialogOpen] = useState(false);
  const [isCustomerInfoChangeDialogOpen, setIsCustomerInfoChangeDialogOpen] =
    useState(false);
  const [showCancelOrderConfirmDialog, setShowCancelOrderConfirmDialog] =
    useState(false);
  const [isViewKOTDrawerOpen, setIsViewKOTDrawerOpen] = useState(false);
  const [isItemDeleteDialogOpen, setIsItemDeleteDialogOpen] = useState(false);
  const [showItemVariantDialog, setShowItemVariantDialog] = useState(false);
  const [selectedOrderType, setSelectedOrderType] = useState({
    type: ORDER_TYPES_VALUES[0].value,
  });
  const [selectedCategory, setSelectedCategory] = useState({});
  const [searchText, setSearchText] = useState("");
  const [noteType, setNoteType] = useState("order");
  const [selectedItemForNote, setSelectedItemForNote] = useState({});
  const [selectedItemForDelete, setSelectedItemForDelete] = useState({});
  const [
    showOrderTypeChangeConfirmDialog,
    setShowOrderTypeChangeConfirmDialog,
  ] = useState(false);
  const [currentOrderType, setCurrentOrderType] = useState({});
  const [selectedItemWithVariants, setSelectedItemWithVariants] = useState([]);
  const cartContainerRef = useRef(null);

  const hasDiscount = orderDiscount && orderDiscount.type !== null;
  const storeId = user?.storeId;

  const mergedItems = useSelector(selectMergedItems);
  const bill = useSelector(selectBillTotals);
  const final = useSelector(selectFinalTotals);

  const handleDialogCancel = () => {
    setIsInstructionsDialogOpen(false);
    setIsDiscountDialogOpen(false);
    setIsTableChangeDialogOpen(false);
    setIsCustomerInfoChangeDialogOpen(false);
  };

  const holdActionClick = () => {};
  const cancelActionClick = () => {
    setShowCancelOrderConfirmDialog(true);
  };

  const handleInstructionsDialog = () => {
    setIsInstructionsDialogOpen(true);
    setNoteType("order");
  };

  const handleApplyDiscount = () => {
    setIsDiscountDialogOpen(true);
  };

  const handleRemoveDiscount = () => {
    dispatch(clearDiscount());
  };

  const handleTableChange = async () => {
    try {
      await dispatch(getTablesWithStatus({ storeId })).unwrap();
      setIsTableChangeDialogOpen(true);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleCustomerInfoChange = () => {
    setIsCustomerInfoChangeDialogOpen(true);
  };

  const handleViewKOT = () => {
    setIsViewKOTDrawerOpen(true);
  };

  const handleCloseViewKOTDrawer = () => {
    setIsViewKOTDrawerOpen(false);
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleRemoveCustomer = () => {
    dispatch(removeOrderCustomer());
  };

  const handleItemNoteClick = (item) => {
    setNoteType("item");
    setSelectedItemForNote(item);
    setIsInstructionsDialogOpen(true);
  };

  // Print
  const receiptRef = useRef();

  const orderData = {
    restaurantName: "My Cafe",
    id: "ORD12345",
    table: "T5",
    waiter: "John",
    date: new Date(),
    items: [
      { name: "Coffee", qty: 2, price: 5 },
      { name: "Sandwich", qty: 1, price: 7 },
    ],
    subtotal: 17,
    discount: 2,
    loyaltyPoints: 1,
    tax: 1.5,
    total: 15.5,
    contact: "Call: 123-456-7890",
  };

  // Add Item to Cart
  const handleAddItems = (itemVariant, item) => {
    if (
      (item.variants && item.variants.length > 1) ||
      item.addons?.length > 0
    ) {
      setSelectedItemWithVariants(item);
      setShowItemVariantDialog(true);
    } else {
      dispatch(addItem({ ...itemVariant, quantity: 1 }));
      dispatch(
        setLastAddedItemId({ id: itemVariant.variant_uid, ts: Date.now() })
      );
    }
  };

  const handleItemRemoveClick = (item, isExisting) => {
    if (isExisting === "existing") {
      setSelectedItemForDelete(item);
      setIsItemDeleteDialogOpen(true);
    } else {
      dispatch(removeItem(item));
    }
  };

  const handleOrderItemDeleteConfirm = async (reason) => {
    console.log("Deleting item for reason:", reason, selectedItemForDelete);
    try {
      const obj = {
        store_id: storeId,
        order_uid: activeOrderId,
        order_id: orderId,
        record_status: "R",
        order_item_id: selectedItemForDelete.order_item_id,
        order_item_uid: selectedItemForDelete.order_item_uid,
        comment: reason,
      };
      const res = await dispatch(deleteOrderItem(obj)).unwrap();
      dispatch(removeItem(selectedItemForDelete));
      setIsItemDeleteDialogOpen(false);
      showSuccess("Item removed successfully");
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleClearCartClick = () => {
    dispatch(clearPendingItems());
  };

  const handleOrderTypeSelected = async (selectedType) => {
    // If order is new, user can change the order type for existing order need to show the confirmation dialog
    setCurrentOrderType(selectedType);
    if (selectedType.type === orderType.type) return;
    if (activeOrderId) {
      if (selectedType.type === ORDER_TYPE_CODES.DINE_IN) {
        try {
          await dispatch(getTablesWithStatus({ storeId })).unwrap();
          setShowOrderTypeChangeConfirmDialog(true);
        } catch (err) {
          handleApiError(err);
        }
      } else {
        setShowOrderTypeChangeConfirmDialog(true);
      }
    } else {
      setShowOrderTypeChangeConfirmDialog(false);
      if (selectedType.type !== ORDER_TYPE_CODES.DINE_IN) {
        dispatch(clearSelectedTable());
      }
      dispatch(setOrderType({ type: selectedType.type }));
    }
  };

  const handleCancelOrderConfirm = async (reason) => {
    try {
      const obj = {
        store_id: storeId,
        order_uid: activeOrderId,
        order_id: orderId,
        order_status_id: ORDER_STATUS.CANCELLED,
        comment: reason.message,
      };
      const res = await dispatch(cancelOrder(obj)).unwrap();
      setShowCancelOrderConfirmDialog(false);
      showSuccess("Order cancelled successfully");
      if (orderType.type !== ORDER_TYPE_CODES.DINE_IN) {
        dispatch(resetCartKeepOrderType());
      } else {
        navigate("/app/tables");
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (orderType?.type) {
      setSelectedOrderType(orderType);
    } else {
      dispatch(setOrderType({ type: ORDER_TYPES_VALUES[0].value }));
    }
  }, [orderType]);

  return (
    <>
      <Helmet>
        <title> Menu | {SITE_TITLE}</title>
      </Helmet>
      <Box style={{ display: "none" }}>
        <AppReceipt ref={receiptRef} order={orderData} />
      </Box>
      <Box className="page-container">
        {/* Categories */}
        <Box>
          <CategoryList
            selectedCategory={selectedCategory}
            onCategoryClick={handleCategoryClick}
            searchTerm={searchText}
          />

          <Box className="items-wrapper">
            <Box className="items-wrapper-content">
              <Box className="menu-items">
                <Box>
                  <Box className="menu-items-search-wrap">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <TextField
                        size="small"
                        autoComplete="T-search-item"
                        placeholder="Search by item"
                        sx={{ width: 250, bgcolor: "#fff", borderRadius: 2 }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                          endAdornment: searchText && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setSearchText("")}
                                edge="end"
                              >
                                <ClearIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {/* <TextField
                        size="small"
                        placeholder="Search by Short code"
                        sx={{ width: 200, bgcolor: "#fff", borderRadius: 2 }}
                      /> */}
                    </Box>

                    {/* Item type legend Veg, Non-veg, Egg */}
                    <Box>
                      <FoodTypeLegend />
                    </Box>
                  </Box>
                </Box>

                <Box className="menu-items-wrap">
                  <Scrollbar
                    sx={{
                      height: 1,
                      "& .simplebar-content": {
                        height: 1,
                        display: "flex",
                        flexDirection: "column",
                      },
                    }}
                  >
                    <Box className="menu-item-content">
                      {Array.isArray(selectedCategory?.products) &&
                      selectedCategory.products.length > 0
                        ? selectedCategory.products.map((item) => (
                            <ItemCard
                              key={item.product_uid}
                              item={item}
                              onAddItem={handleAddItems}
                            />
                          ))
                        : !isLoading && (
                            <EmptyCategoryItems
                              onClearSearch={() => setSearchText("")}
                            />
                          )}
                    </Box>
                  </Scrollbar>
                </Box>
              </Box>

              <Box className="menu-items-card">
                <Box>
                  <Box>
                    {/* Right - Card items order type selector */}
                    <OrderTypeSelector
                      selectedOrderType={selectedOrderType}
                      onOrderTypeSelected={handleOrderTypeSelected}
                    />
                  </Box>

                  <Box className="menu-table-content-wrap">
                    {/* Right - Card items order info  */}
                    <OrderInfoBar
                      onCancelClick={cancelActionClick}
                      onHoldClick={holdActionClick}
                      onTableClick={handleTableChange}
                      onCustomerClick={handleCustomerInfoChange}
                      onRemoveCustomer={handleRemoveCustomer}
                      onClearClick={handleClearCartClick}
                    />
                  </Box>
                </Box>

                <Box>
                  <Box className="menu-item-header">
                    <Box className="menu-item-items">ITEMS</Box>
                    <Box className="menu-item-qty">QTY.</Box>
                    <Box className="menu-item-price">PRICE</Box>
                  </Box>
                  <Box
                    sx={{
                      marginBottom: "15px",
                    }}
                    ref={cartContainerRef}
                  >
                    {existingKots.length > 0 && (
                      <Box>
                        {existingKots.map((kotGroup) => (
                          <Box
                            key={kotGroup.order_kot_uid}
                            sx={{
                              marginBottom: "8px",
                              background:
                                kotGroup.kot_number !== "NO_KOT"
                                  ? "#FAFAFA"
                                  : "#FFFBEA",
                            }}
                          >
                            {/* KOT Badge */}
                            <Box
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box
                                sx={{
                                  background:
                                    kotGroup.kot_number !== "NO_KOT"
                                      ? "#E9F3FF"
                                      : "#FFF3CD",
                                  color: "#0A4C9C",
                                  border: "1px solid",
                                  borderColor:
                                    kotGroup.kot_number !== "NO_KOT"
                                      ? "#C8E0FF"
                                      : "#FFECB5",
                                  padding: "3px 10px",
                                  fontSize: "12px",
                                  display: "inline-block",
                                  borderRadius: "6px",
                                  fontWeight: 600,
                                }}
                              >
                                {kotGroup.kot_number !== "NO_KOT"
                                  ? `KOT #${kotGroup.kot_number} • ${formatTime(
                                      kotGroup.created_at
                                    )}`
                                  : "Non-KOT Items"}
                              </Box>
                              <IconButton
                                size="small"
                                style={{
                                  marginRight: "8px",
                                }}
                              >
                                <Tooltip title="Print KOT Items">
                                  <Iconify
                                    icon="material-symbols:print-rounded"
                                    width={16}
                                  />
                                </Tooltip>
                              </IconButton>
                            </Box>
                            {/* KOT Items */}
                            {kotGroup.items.map((item, index) => (
                              <OrderMenuItem
                                key={item.variant_uid}
                                item={item}
                                onUpdate={(updated) =>
                                  dispatch(updateItem(updated))
                                }
                                onRemove={(item) =>
                                  handleItemRemoveClick(item, "existing")
                                }
                                onNoteClick={handleItemNoteClick}
                                index={index}
                                kotId={kotGroup.order_kot_uid}
                              />
                            ))}
                            {/* KOT kits instructions button */}
                            {kotGroup?.comment && (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: "2px",
                                  alignItems: "center",
                                  margin: "8px 20px",
                                }}
                              >
                                <Tooltip title="Edit Instructions">
                                  <IconButton size="small">
                                    <Iconify
                                      icon={"hugeicons:note-edit"}
                                      onClick={handleInstructionsDialog}
                                    />
                                  </IconButton>
                                </Tooltip>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "12px",
                                    lineHeight: "1.0rem",
                                  }}
                                >
                                  {kotGroup?.comment}
                                </Typography>
                                <IconButton
                                  onClick={() => dispatch(removeNote())}
                                  size="small"
                                >
                                  <Tooltip title="Remove">
                                    <Iconify icon="mdi:close" width={16} />
                                  </Tooltip>
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {pendingItems.length > 0 && (
                      <Box>
                        <Box
                          sx={{
                            background: "#FFF4E5",
                            color: "#B55A00",
                            border: "1px solid #FFE0B3",
                            padding: "3px 10px",
                            fontSize: "12px",
                            display: "inline-block",
                            borderRadius: "6px",
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          NEW KOT
                        </Box>
                        {pendingItems?.map((item, index) => (
                          <OrderMenuItem
                            key={item.cartItemId}
                            item={item}
                            lastAddedItemId={lastAddedItemId}
                            onUpdate={(updated) =>
                              dispatch(updateItem(updated))
                            }
                            onRemove={handleItemRemoveClick}
                            onNoteClick={handleItemNoteClick}
                            onVariantEdit={() => console.log("Edit variant")}
                            index={index}
                            kotId={0}
                          />
                        ))}
                      </Box>
                    )}
                    {pendingItems.length === 0 && existingKots.length === 0 && (
                      <Box>
                        <EmptyCartState />
                      </Box>
                    )}
                  </Box>
                </Box>

                {itemsNote && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "2px",
                      alignItems: "center",
                      margin: "8px 20px",
                    }}
                  >
                    <Tooltip title="Edit Instructions">
                      <IconButton size="small">
                        <Iconify
                          icon={"hugeicons:note-edit"}
                          onClick={handleInstructionsDialog}
                        />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "12px",
                        lineHeight: "1.0rem",
                      }}
                    >
                      {itemsNote}
                    </Typography>
                    <IconButton
                      onClick={() => dispatch(removeNote())}
                      size="small"
                    >
                      <Tooltip title="Remove">
                        <Iconify icon="mdi:close" width={16} />
                      </Tooltip>
                    </IconButton>
                  </Box>
                )}

                {(pendingItems.length > 0 || existingKots.length > 0) && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    {!itemsNote && pendingItems.length > 0 && (
                      <Tooltip
                        title={
                          itemsNote
                            ? "Edit instructions for kitchen"
                            : "Add instructions for kitchen"
                        }
                      >
                        <Button
                          size="small"
                          className="add-note-wrap"
                          onClick={handleInstructionsDialog}
                        >
                          <Iconify icon={"hugeicons:note-add"} />
                          <Box>
                            {itemsNote
                              ? "Edit Instructions"
                              : "Add Instructions"}
                          </Box>
                        </Button>
                      </Tooltip>
                    )}
                    {activeOrderId && (
                      <Tooltip title="View KOT">
                        <Button
                          size="small"
                          className="add-note-wrap"
                          onClick={handleViewKOT}
                          sx={{
                            marginLeft: "auto !important",
                          }}
                        >
                          <Iconify icon={"carbon:view"} />
                          <Box>View KOT</Box>
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                )}

                <Box>
                  {(pendingItems.length > 0 || existingKots.length > 0) && (
                    <OrderBillDetails />
                  )}
                </Box>

                {(pendingItems.length > 0 || existingKots.length > 0) && (
                  <Box>
                    <Box
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        margin: 20,
                        fontSize: 15,
                      }}
                    >
                      {!hasDiscount ? (
                        <Tooltip title="Apply Discount">
                          <Button
                            onClick={handleApplyDiscount}
                            startIcon={<Iconify icon="material-symbols:add" />}
                          >
                            Apply Discount
                          </Button>
                        </Tooltip>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            px={1.5}
                            py={0.5}
                            borderRadius={1.5}
                            sx={{
                              backgroundColor: "rgba(0, 123, 255, 0.1)", // light blue
                              color: "#0055aa",
                              fontSize: "0.85rem",
                              fontWeight: 500,
                            }}
                          >
                            <Iconify icon="mdi:tag-outline" width={16} />
                            <span>
                              ₹{final.discountAmount} discount applied
                            </span>
                          </Box>

                          <Tooltip title="Remove Discount">
                            <IconButton
                              onClick={handleRemoveDiscount}
                              size="small"
                            >
                              <Iconify
                                icon="material-symbols:close-rounded"
                                width={16}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Order Actions*/}
                <OrderActions itemTotal={final.payable} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {isInstructionsDialogOpen && (
        <InstructionsDialog
          noteType={noteType}
          isInstructionsDialogOpen={isInstructionsDialogOpen}
          onDialogCancel={handleDialogCancel}
          itemsNote={itemsNote}
          item={selectedItemForNote}
        />
      )}

      {isDiscountDialogOpen && (
        <DiscountDialog
          isDiscountDialogOpen={isDiscountDialogOpen}
          onDialogCancel={handleDialogCancel}
        />
      )}

      {isTableChangeDialogOpen && (
        <TableChangeDialog
          isTableChangeDialogOpen={isTableChangeDialogOpen}
          onDialogCancel={handleDialogCancel}
        />
      )}

      {isCustomerInfoChangeDialogOpen && (
        <CustomerInfoChangeDialog
          isCustomerInfoChangeDialogOpen={isCustomerInfoChangeDialogOpen}
          onDialogCancel={handleDialogCancel}
          customerDetails={customerDetails}
        />
      )}

      {showCancelOrderConfirmDialog && (
        <OrderCancelConfirmDialog
          open={showCancelOrderConfirmDialog}
          onClose={() => setShowCancelOrderConfirmDialog(false)}
          onConfirm={handleCancelOrderConfirm}
        />
      )}
      {isItemDeleteDialogOpen && (
        <OrderItemDeleteConfirmDialog
          open={isItemDeleteDialogOpen}
          onClose={() => setIsItemDeleteDialogOpen(false)}
          onConfirmDelete={handleOrderItemDeleteConfirm}
        />
      )}

      {showOrderTypeChangeConfirmDialog && (
        <OrderTypeChangeConfirmationDialog
          open={showOrderTypeChangeConfirmDialog}
          onDialogCancel={() => setShowOrderTypeChangeConfirmDialog(false)}
          orderType={orderType}
          currentOrderType={currentOrderType}
          selectedTable={selectedTable}
        />
      )}

      {showItemVariantDialog && (
        <ItemVariantSelectionDialog
          open={showItemVariantDialog}
          onClose={() => setShowItemVariantDialog(false)}
          item={selectedItemWithVariants}
          onAddToCart={(item) => {
            dispatch(addItem(item));
            setShowItemVariantDialog(false);
          }}
        />
      )}
      {isViewKOTDrawerOpen && (
        <OrderKOTViewDrawer
          isOpen={isViewKOTDrawerOpen}
          closeOrderKOTDrawer={handleCloseViewKOTDrawer}
          activeOrderId={activeOrderId}
          orderId={orderId}
          storeId={storeId}
        />
      )}
    </>
  );
}

export default Menu;
